import { JSONSchema7 } from 'json-schema';
import { customSchemas, types as commonTypes } from '@sitebuilder/common';
import Link from './Link';

const schema: JSONSchema7 = {
    title: 'Link',
    type: 'object',
    required: ['link'],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        navigateWithinPage: {
            type: 'boolean',
            title: 'Navigate within page',
            default: false,
            description: 'Navigate within page',
        },
    },
    dependencies: {
        showIcon: {
            oneOf: [
                {
                    properties: {
                        showIcon: { enum: [true] },
                        iconName: {
                            type: 'string',
                            title: 'Icon Name',
                        },
                        iconPosition: {
                            type: 'string',
                            title: 'Icon Position',
                            enum: ['Right', 'Left'],
                            default: 'Left',
                        },
                    },
                    required: ['iconName'],
                },
                {
                    properties: {
                        showIcon: { enum: [false] },
                    },
                    required: ['linkName'],
                },
            ],
        },
        navigateWithinPage: {
            oneOf: [
                {
                    properties: {
                        navigateWithinPage: { enum: [true] },
                        isWithinIframe: {
                            type: 'boolean',
                            title: 'Is within iframe',
                            description: 'Navigate within iframe with link',
                        },
                        linkHref: {
                            type: 'string',
                            title: 'Link href',
                            description: 'Id for the element to navigate to',
                        },
                        scrollOptions: {
                            type: 'string',
                            title: 'Scroll Options',
                            description: 'Add options for react-scroll',
                            default: '',
                        },
                    },
                    required: ['linkHref'],
                    dependencies: {
                        isWithinIframe: {
                            oneOf: [
                                {
                                    properties: {
                                        isWithinIframe: { enum: [true] },
                                        linkClassName: {
                                            type: 'string',
                                            title: 'Link class name',
                                            default: 'nav-link',
                                        },
                                    },
                                },
                                {
                                    properties: {
                                        isWithinIframe: { enum: [false] },
                                    },
                                },
                            ],
                        },
                    },
                },
                {
                    properties: {
                        navigateWithinPage: { enum: [false] },
                        link: {
                            type: 'string',
                            title: 'Link',
                            description: 'URL for the link',
                        },
                        linkName: {
                            type: 'string',
                            title: 'Link Name',
                            description: 'Link Name',
                        },
                        showIcon: {
                            type: 'boolean',
                            enum: [true, false],
                            description: 'Show Icon on side of link or not',
                            default: false,
                            title: 'Show Icon',
                        },
                        openInNewTab: {
                            type: 'boolean',
                            default: false,
                            title: 'Open link in new tab',
                        },
                        target: {
                            type: 'string',
                            title: 'Target for <a> tag',
                            description: 'Can either be _blank, _self, _parent, _top',
                        },
                        queryParameters: {
                            type: 'array',
                            description: 'Add query parameters',
                            title: 'Construct a query parameter',
                            items: {
                                type: 'object',
                                properties: {
                                    queryParameter: {
                                        type: 'string',
                                        title: 'Query parameter',
                                    },
                                    queryValue: {
                                        type: 'string',
                                        title: 'Query value',
                                    },
                                },
                            },
                        },
                    },
                    required: ['link'],
                },
            ],
        },
        isWithinIframe: {
            oneOf: [
                {
                    properties: {
                        isWithinIframe: { enum: [true] },
                        linkClassName: {
                            type: 'string',
                            title: 'Link class name',
                            default: 'nav-link',
                        },
                        linkHref: {
                            type: 'string',
                            title: 'Link href',
                            description: 'Id for the element to navagite to',
                        },
                    },
                    required: ['linkHref'],
                },
                {
                    properties: {
                        isWithinIframe: { enum: [false] },
                    },
                    required: ['link'],
                },
            ],
        },
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
    },
};

const groups = [
    {
        'Style Editor': ['styles'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Values: [
            'navigateWithinPage',
            'link',
            'linkName',
            'showIcon',
            'iconName',
            'iconPosition',
            'openInNewTab',
            'target',
            'queryParameters',
            'linkHref',
            'scrollOptions',
            'isWithinIframe',
            'linkClassName',
        ],
    },
];

const uiSchema = {
    'ui:groups': groups,
    ...customSchemas.textSchema.TextSchema.uiSchema,
    ...customSchemas.styleSchema.StyleSchema.uiSchema,
    ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
    styles: {
        'ui:widget': 'CSSEditor',
    },
    iconPosition: {
        'ui:widget': 'radio',
    },
    link: {
        'ui:widget': 'CustomTextInput',
    },
    target: {
        'ui:widget': 'CustomTextInput',
    },
    showIcon: {
        'ui:widget': 'CustomCheckbox',
    },
    openInNewTab: {
        'ui:widget': 'CustomCheckbox',
    },
    linkName: {
        'ui:widget': 'ResourcePicker',
    },
    navigateWithinPage: {
        'ui:widget': 'CustomCheckbox',
    },
    isWithinIframe: {
        'ui:widget': 'CustomCheckbox',
    },
    linkClassName: {
        'ui:disabled': true,
        'ui:widget': 'CustomTextInput',
    },
    linkHref: {
        'ui:widget': 'CustomTextInput',
    },
    queryParameters: {
        items: {
            queryParameter: {
                'ui:widget': 'CustomTextInput',
            },
            queryValue: {
                'ui:widget': 'CustomTextInput',
            },
        },
    },
    scrollOptions: {
        'ui:widget': 'CustomTextInput',
    },
};

const LinkWidget: commonTypes.IWidget = {
    schema,
    uiSchema,
    templateView: Link,
    id: 'Link',
    group: 'button',
    contentPlaceHolders: ['children'],
    initialData: () => ({
        linkName: 'link',
        link: '',
    }),
};

export default LinkWidget;
